import { isTopicRequestSuperseded, bindTopicMessageApi } from '../../components/common/chat/topics/useChatTopics';
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { ADMIN_MESSAGE_FORMATS, isHoldAnnouncementFormat, isInvoiceResubmissionRequestedFormat, isPartAnnouncementFormat, isStageSeatingShareAnnouncementFormat } from '../../constants/messageFormats';
import { buildArtistProfileForModal, firstNonEmpty } from '../../components/common/chat/members/memberModalProfileUtils';
import ChatMessageSelectableCopyArea from '../../components/common/chat/timeline/ChatMessageSelectableCopyArea';
import useViewportBoundedMenuPosition from '../../components/common/chat/shared/useViewportBoundedMenuPosition';
import { focusChatAnchorMessage } from '../../components/common/chat/timeline/chatMessageAnchorUtils';
import { supportsChatMessageReaction } from '../../components/common/chat/reactions/chatMessageReactionUtils';
import { hasCopyableChatMessageText, resolveCopyableChatMessageText, writeTextToClipboard } from '../../components/common/chat/timeline/chatMessageCopyUtils';
import { isWithinHoursFromNow, sidebarTimeLabelJST, toYmdSlash } from '../../utils/datetime';
import { parseReminderTags, sanitizeReminderContent } from '../../components/common/chat/timeline/systemMessageUtils';
import { appendVariant } from '../../components/common/chat/composer/chatAssetUtils';
import { appendBubbleFormatClassName } from '../../components/common/chat/timeline/messageBubbleClassUtils';
import { shouldExcludeAdminSenderForMessage } from '../../components/common/chat/timeline/adminSenderExclusionUtils';
import {
  normalizeStageSeatingShareUrlForAdmin,
  replaceStageSeatingShareUrlsForAdmin,
} from '../../components/common/chat/stage-seating/stageSeatingShareLinkUtils';
import { listArtistGroups, listParts } from '../../api/artists';
import { getEventDetail } from '../../api/taskEvents';
import { listGenres } from '../../api/genres';
import { getReminderSchedule } from '../../api/reminders';
import { deleteAdminMessage, getAdminUser, getAdminArtistChatNotifyMute, getAdminChatArtistWithParts, getAdminReadReceiptSetting, listAdminMessages as rawListAdminMessages, listAdminMessagesAround as rawListAdminMessagesAround, listAdminMessagesBefore as rawListAdminMessagesBefore, listAdminNicknamePermissions, searchAdminChatArtistsWithPartsPaged } from '../../api/adminChat';
import { ARTIST_PROFILE_IMAGE_PATH, buildAssetUrl, buildFlyerUrl, ADMIN_ARTIST_ASSIGNEE_FILTER_STORAGE_KEY, ADMIN_ARTIST_PINNED_TARGETS_STORAGE_KEY, normalizeArtistId, normalizePinnedArtistIds, isSameArtistId, normalizeMessageId, wait, getMessageDedupKey, dedupeMessages, UNSENDABLE_FORMATS } from '../../components/admin/chats/chatPageHelpers';
import { mergeMessages } from '../../components/common/chat/timeline/timelineMerge';
import { normalizeAvatarImageValue } from '../../components/common/avatar/PersonAvatar';
export default function useAdminChatMessageSupport(context) {
  const {
    chatTopics,    ARTISTS_PAGE_SIZE,
    Error,
    READ_MARK_DEDUP_WINDOW_MS,
    activeAnchorMessageId,
    allowAutoAppend,
    anchorHighlightTimerRef,
    artistDetail,
    artistId,
    artistMuteCacheRef,
    artistOffset,
    artistTargetAccessDeniedRef,
    artistTargetsRetryTimerRef,
    artists,
    artistsFetchSeqRef,
    artistsHasMore,
    artistsLoading,
    artistsLoadingRef,
    artistsSentinelInView,
    assigneeFilter,
    bumpReadOptInVersion,
    canViewArtistTargets,
    canViewPersonalChatForSession,
    chatContainerRef,
    checking,
    closeProfileModal,
    fetchInitialMessagesRef,
    fetchSession,
    flyerCacheRef,
    genres,
    genresByIdRef,
    genresByValueLowerRef,
    groupFilter,
    hasMore,
    hasNewerMessages,
    initialSelectionDoneRef,
    inlineCopyMessageId,
    isAnnouncedMessage,
    isFetching,
    isMobile,
    isReadOnly,
    limit,
    loadedArtistIdsRef,
    loadingNewerMessages,
    localStorage,
    location,
    longPressTimerRef,
    messageFilter,
    messageMenu,
    messageWindowOffset,
    messages,
    mobileChatHistoryRef,
    mobileListPrefRef,
    mobileView,
    nameFilter,
    castingFilter,
    navigate,
    normalizeArtistSidebarRow,
    oldestCursor,
    openArtistProfile,
    partFilter,
    pendingArtistIdsRef,
    pendingRestoreRef,
    pinAnnouncement,
    pinnedArtistIds,
    prevSelectedArtistIdRef,
    readMarkCompletedAtRef,
    readMarkInFlightRef,
    readOptInCacheRef,
    reconcileTimerRef,
    reminderCacheRef,
    selectedArtist,
    selectedArtistId,
    selectedArtistIdRef,
    selectedArtistRef,
    sessionStorage,
    setActiveAnchorMessageId,
    setArtistChatMuteLoading,
    setArtistChatMuted,
    setArtistOffset,
    setArtists,
    setArtistsHasMore,
    setArtistsLoading,
    setAssigneeFilter,
    setFlyerCacheVersion,
    setGenres,
    setGroups,
    setHasMore,
    setHasNewerMessages,
    setInlineCopyMessageId,
    setIsAiTargetSelected,
    setIsAnnouncementListOpen,
    setIsFetching,
    setIsInitialLoading,
    setIsNicknameAllowed,
    setIsReadNotifyEnabled,
    setLoadedMessagesArtistId,
    setLoadingNewerMessages,
    setLoadingOlderMessages,
    setMessageMenu,
    setMessageWindowOffset,
    setMessages,
    setMobileView,
    setOldestCursor,
    setParts,
    setQuickTaskSource,
    setReadEnabledAdmins,
    setReadNotifyFetching,
    setReminderCacheVersion,
    setReplyDraft,
    setSelectedArtist,
    setTargetMenu,
    settingsArtistIdRef,
    sidebarRef,
    targetMenu,
    toastError,
    toastSuccess,
    toggleReaction,
    unpinAnnouncement,
    user,
  } = context;
  const listAdminMessagesBefore = useCallback(bindTopicMessageApi(chatTopics, rawListAdminMessagesBefore, 3), [chatTopics]);
  const listAdminMessagesAround = useCallback(bindTopicMessageApi(chatTopics, rawListAdminMessagesAround, 4), [chatTopics]);
  const listAdminMessages = useCallback(bindTopicMessageApi(chatTopics, rawListAdminMessages, 3), [chatTopics]);

  // ====== 表示フォーマット系（サイドバー相対表記以外はユーティリティ使用） ======
  const formatSidebarTimestamp = (dbString) => {
    return sidebarTimeLabelJST(dbString);
  };

  const getArtistLatestTimestamp = useCallback((artist) => {
    if (!artist) return null;
    // 実メッセージ由来の時刻を最優先にし、旧来の last_message_time はフォールバックとして扱う。
    return artist.last_message || artist.last_message_time || null;
  }, []);

  const getSidebarPreviewText = (artist) => {
    const fmt = artist.last_message_format;
    const typ = artist.last_message_type;
    const fromMe = typ === 'admin_message';
    if (isInvoiceResubmissionRequestedFormat(fmt)) {
      return '請求書の再発行依頼を送信しました。';
    }
    switch (fmt) {
      case ADMIN_MESSAGE_FORMATS.IMAGE:
        return fromMe ? '画像を送信しました。' : '画像が届きました。';
      case ADMIN_MESSAGE_FORMATS.FILE:
        return fromMe ? 'ファイルを送信しました。' : 'ファイルが届きました。';
      case ADMIN_MESSAGE_FORMATS.ANNOUNCE_HOLD:
        return '「オファー」を送信しました。';
      case ADMIN_MESSAGE_FORMATS.ANNOUNCE_HOLD_CANCELLED:
        return fromMe ? 'オファーをキャンセルしました。' : 'オファーがキャンセルされました。';
      case ADMIN_MESSAGE_FORMATS.ANNOUNCE_HOLD_DECLINE:
        return fromMe ? '不参加通知を送信しました。' : '不参加通知が届きました。';
      case ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT:
        return fromMe ? '「本契約」を送信しました。' : '「本契約」が届きました。';
      case ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT_CANCELLED:
        return fromMe ? '本契約をキャンセルしました。' : '本契約がキャンセルされました。';
      case ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT_UPDATED:
        return fromMe ? '契約内容を更新しました。' : '契約内容が更新されました。';
      case ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONCIERGE_RESULT:
        return fromMe ? 'コンシェルジュ申込の結果を送信しました。' : 'コンシェルジュ申込の結果が届きました。';
      case ADMIN_MESSAGE_FORMATS.ANNOUNCE_CANCEL_COMP_DECIDED:
        return '中止保証金額が確定しました。';
      case ADMIN_MESSAGE_FORMATS.ADDITIONAL_REWARD_OFFER:
        return '追加支払いの確認依頼を送信しました。';
      case ADMIN_MESSAGE_FORMATS.ANNOUNCE_ADDITIONAL_PAYMENT_CANCELLED:
        return '追加支払いの確認依頼を取り消しました。';
      case ADMIN_MESSAGE_FORMATS.ADDITIONAL_REWARD_CLAIM_COMPLETED:
        return '追加請求の審査結果を送信しました。';
      case ADMIN_MESSAGE_FORMATS.ANNOUNCE_PART_ASSIGNED:
        return '参加パートが確定しました。';
      case ADMIN_MESSAGE_FORMATS.ANNOUNCE_PART_CHANGED:
        return '参加パートが変更されました。';
      case ADMIN_MESSAGE_FORMATS.ANNOUNCE_PART_UNASSIGNED:
        return '参加パートが未割り当てになりました。';
      case ADMIN_MESSAGE_FORMATS.ANNOUNCE_STAGE_SEATING_SHARED:
      case ADMIN_MESSAGE_FORMATS.STAGE_SEATING_SHARED:
        return 'ステージシーティングを共有しました。';
      case ADMIN_MESSAGE_FORMATS.REMINDER_NOTICE:
        return 'リマインドしました。';
      case ADMIN_MESSAGE_FORMATS.NOTE_CREATE:
        return 'ノートが作成されました。';
      case ADMIN_MESSAGE_FORMATS.NOTE_UPDATE:
        return 'ノートが修正されました。';
      case ADMIN_MESSAGE_FORMATS.NOTE_DELETE:
        return 'ノートが削除されました。';
      case ADMIN_MESSAGE_FORMATS.TIMETABLE_CREATED:
        return 'タイムテーブルが作成されました。';
      case 'timetable_updated':
        return 'タイムテーブルが更新されました。';
      case ADMIN_MESSAGE_FORMATS.EVENT_MATERIAL_SHARED:
        return '共有資料がアーティストに共有されました。';
      case ADMIN_MESSAGE_FORMATS.EVENT_IMAGES_ADDED:
        return 'イベントアルバムが共有されました。';
      case ADMIN_MESSAGE_FORMATS.EVENT_INFO_UPDATED:
        return 'イベントの情報が更新または追加されました。';
      case ADMIN_MESSAGE_FORMATS.EVENT_CANCELLED:
        return 'イベントが中止となりました。';
      case 'system_notice':
        return 'システム通知が届きました。';
      case ADMIN_MESSAGE_FORMATS.TEXT:
      default:
        return artist.last_message_text || '';
    }
  };

  const isSystemFormat = (fmt) =>
    fmt &&
    fmt !== ADMIN_MESSAGE_FORMATS.TEXT &&
    fmt !== ADMIN_MESSAGE_FORMATS.IMAGE &&
    fmt !== ADMIN_MESSAGE_FORMATS.FILE;

  const canUnsendMessage = useCallback(
    (message) => {
      if (isReadOnly) return false;
      if (!message) return false;
      if (message.message_type !== 'admin_message') return false;
      const adminId = user?.userId ?? user?.id ?? null;
      if (adminId && String(message.admin_user_id) !== String(adminId)) return false;
      if (!UNSENDABLE_FORMATS.has(message.message_format)) return false;
      return isWithinHoursFromNow(message.created_at, 1);
    },
    [isReadOnly, user],
  );

  const canAnnouncementMessage = useCallback((message) => {
    if (!message) return false;
    return UNSENDABLE_FORMATS.has(message.message_format);
  }, []);

  const canReactMessage = useCallback((message) => {
    if (!message || !supportsChatMessageReaction(message)) return false;
    return String(message.message_type || '').trim() !== 'admin_message';
  }, []);

  const canReplyMessage = useCallback((message) => {
    if (!message) return false;
    const type = String(message.message_type || '').trim();
    const format = String(message.message_format || '').trim().toLowerCase();
    return ['admin_message', 'artist_message'].includes(type) && ['text', 'image', 'file'].includes(format);
  }, []);

  const canCopyMessage = useCallback((message) => hasCopyableChatMessageText(message), []);

  const canCreateQuickTaskFromMessage = useCallback((message) => {
    if (!message) return false;
    return ['admin_message', 'artist_message'].includes(String(message.message_type || '').trim());
  }, []);

  const canOpenMessageMenu = useCallback(
    (message) =>
      canCopyMessage(message) ||
      canReplyMessage(message) ||
      canUnsendMessage(message) ||
      canAnnouncementMessage(message) ||
      canReactMessage(message) ||
      canCreateQuickTaskFromMessage(message),
    [
      canAnnouncementMessage,
      canCopyMessage,
      canCreateQuickTaskFromMessage,
      canReactMessage,
      canReplyMessage,
      canUnsendMessage,
    ],
  );

  const { menuRef: messageMenuRef, resolveMenuPosition } = useViewportBoundedMenuPosition({
    menu: messageMenu,
    setMenu: setMessageMenu,
    padding: 12,
    fallbackWidth: 248,
    fallbackHeight: 360,
  });
  const { menuRef: targetMenuRef, resolveMenuPosition: resolveTargetMenuPosition } = useViewportBoundedMenuPosition({
    menu: targetMenu,
    setMenu: setTargetMenu,
    padding: 12,
    fallbackWidth: 220,
    fallbackHeight: 120,
  });

  const closeMessageMenu = useCallback(() => {
    setMessageMenu(null);
  }, []);

  const closeTargetMenu = useCallback(() => {
    setTargetMenu(null);
  }, []);

  const closePartialCopyMode = useCallback(() => {
    setInlineCopyMessageId('');
  }, []);

  const openMessageMenuAt = useCallback(
    (message, x, y) => {
      if (!message) return;
      const pos = resolveMenuPosition(x, y);
      setMessageMenu({ message, x: pos.x, y: pos.y });
    },
    [resolveMenuPosition],
  );

  const openTargetMenuAt = useCallback(
    (artist, x, y) => {
      if (!artist) return;
      const pos = resolveTargetMenuPosition(x, y);
      setTargetMenu({ artist, x: pos.x, y: pos.y });
    },
    [resolveTargetMenuPosition],
  );

  const clearMessageLongPress = useCallback(() => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  }, []);

  const handleMessageContextMenu = useCallback(
    (event, message) => {
      if (!canOpenMessageMenu(message)) return;
      event.preventDefault();
      openMessageMenuAt(message, event.clientX, event.clientY);
    },
    [canOpenMessageMenu, openMessageMenuAt],
  );

  const handleMessageTouchStart = useCallback(
    (event, message) => {
      if (!isMobile || !canOpenMessageMenu(message)) return;
      clearMessageLongPress();
      const touch = event.touches?.[0];
      if (!touch) return;
      const { clientX, clientY } = touch;
      longPressTimerRef.current = setTimeout(() => {
        openMessageMenuAt(message, clientX, clientY);
      }, 550);
    },
    [clearMessageLongPress, openMessageMenuAt, canOpenMessageMenu, isMobile],
  );

  const handleUnsendMessage = useCallback(async () => {
    const target = messageMenu?.message;
    if (!target) return;
    closeMessageMenu();
    try {
      const data = await deleteAdminMessage(String(target.id));
      if (data.success === false) {
        throw new Error(data?.message || '送信取り消しに失敗しました。');
      }
      const deletedIds = Array.isArray(data?.messageIds) && data.messageIds.length
        ? data.messageIds.map((id) => String(id))
        : [String(target.id)];
      const deletedSet = new Set(deletedIds);
      setMessages((prev) => prev.filter((m) => !deletedSet.has(String(m.id))));
    } catch (err) {
      if (toastError) toastError(err?.message || '送信取り消しに失敗しました。');
    }
  }, [messageMenu, closeMessageMenu, toastError]);

  const handleToggleAnnouncementMessage = useCallback(async () => {
    const target = messageMenu?.message;
    if (!target) return;
    closeMessageMenu();
    try {
      if (isAnnouncedMessage(target.id)) {
        await unpinAnnouncement(target.id);
      } else {
        await pinAnnouncement(target.id);
      }
    } catch (_) {}
  }, [closeMessageMenu, isAnnouncedMessage, messageMenu, pinAnnouncement, unpinAnnouncement]);

  const handleMessageMenuReactionSelect = useCallback(async (messageId, reactionType) => {
    closeMessageMenu();
    try {
      await toggleReaction(messageId, reactionType);
    } catch (_) {}
  }, [closeMessageMenu, toggleReaction]);

  const handleCopyMessage = useCallback(async () => {
    const target = messageMenu?.message;
    if (!target) return;
    const copyText = resolveCopyableChatMessageText(target);
    closeMessageMenu();
    if (!copyText) {
      toastError?.('コピーできる本文がありません。');
      return;
    }
    try {
      await writeTextToClipboard(copyText);
      toastSuccess?.('メッセージをコピーしました。');
    } catch (error) {
      toastError?.(error?.message || 'コピーに失敗しました。');
    }
  }, [closeMessageMenu, messageMenu, toastError, toastSuccess]);

  const handleOpenInlinePartialCopy = useCallback(() => {
    const target = messageMenu?.message;
    if (!target) return;
    const copyText = resolveCopyableChatMessageText(target);
    closeMessageMenu();
    if (!copyText) {
      toastError?.('コピーできる本文がありません。');
      return;
    }
    const nextMessageId = normalizeMessageId(target.id);
    setInlineCopyMessageId((current) => (current === nextMessageId ? '' : nextMessageId));
  }, [closeMessageMenu, messageMenu, toastError]);

  const handleOpenQuickTaskFromMessage = useCallback(() => {
    const target = messageMenu?.message;
    if (!target) return;
    const text = resolveCopyableChatMessageText(target) || target.content || target.text || '';
    const artistId = target.artist_id || selectedArtist?.artist_id || null;
    setQuickTaskSource({
      type: 'chat_artist',
      id: artistId ? String(artistId) : null,
      messageId: target.id ? String(target.id) : null,
      scope: 'admin_artist_chat',
      title: selectedArtist?.name ? `${selectedArtist.name} とのチャット` : 'アーティストチャット',
      excerpt: text,
      messageText: text,
      artistId: artistId ? String(artistId) : null,
    });
    closeMessageMenu();
  }, [closeMessageMenu, messageMenu, selectedArtist]);

  const handleCopySelectionText = useCallback(async (selectedText) => {
    if (!String(selectedText || '').trim()) {
      toastError?.('コピーしたい範囲を選択してください。');
      return;
    }
    try {
      await writeTextToClipboard(selectedText);
      toastSuccess?.('選択範囲をコピーしました。');
    } catch (error) {
      toastError?.(error?.message || 'コピーに失敗しました。');
    }
  }, [toastError, toastSuccess]);

  const renderPartialCopyContent = useCallback((message, contentNode, isSelfMessage = false) => {
    const normalizedMessageId = normalizeMessageId(message?.id);
    if (!normalizedMessageId || !canCopyMessage(message)) return contentNode;
    return (
      <ChatMessageSelectableCopyArea
        active={inlineCopyMessageId === normalizedMessageId}
        messageId={normalizedMessageId}
        copyButtonSide={isSelfMessage ? 'left' : 'right'}
        onDeactivate={closePartialCopyMode}
        onCopySelection={handleCopySelectionText}
      >
        {contentNode}
      </ChatMessageSelectableCopyArea>
    );
  }, [canCopyMessage, closePartialCopyMode, handleCopySelectionText, inlineCopyMessageId]);

  const buildReplyDraftFromMessage = useCallback((message) => {
    if (!message) return null;
    const format = String(message.message_format || '').trim().toLowerCase();
    return {
      messageId: normalizeMessageId(message.id),
      senderName:
        message.message_type === 'admin_message'
          ? String(message.admin_name || '管理者').trim()
          : String(message.artist_name || selectedArtist?.name || 'アーティスト').trim(),
      type: format,
      previewText: format === 'text' ? String(message.content || '').trim() : '',
      fileName:
        format === 'file'
          ? String(message.original_name || message.stored_file_name || '').trim()
          : '',
    };
  }, [selectedArtist]);

  const handleSelectReplyMessage = useCallback(() => {
    const target = messageMenu?.message;
    if (!target) return;
    closeMessageMenu();
    const draft = buildReplyDraftFromMessage(target);
    const topic = target.topic || (target.topic_id ? { id: target.topic_id, name: target.topic_name || 'スレッド' } : null);
    if (String(topic?.id || '') !== chatTopics.topicId) {
      chatTopics.pendingReplyDraft = draft;
      chatTopics.selectTopic(topic);
    }
    setReplyDraft(draft);
  }, [buildReplyDraftFromMessage, closeMessageMenu, messageMenu]);

  const handleSelectAnnouncementMessage = async (messageId) => {
    const normalizedMessageId = normalizeMessageId(messageId);
    const targetArtistId = selectedArtistIdRef.current;
    if (!targetArtistId || !normalizedMessageId) return;
    setIsAnnouncementListOpen(false);
    await fetchInitialMessages(targetArtistId, {
      force: true,
      anchorMessageId: normalizedMessageId,
    });
  };

  const handleJumpToReplyMessage = useCallback(async (reply) => {
    const normalizedMessageId = normalizeMessageId(reply?.messageId);
    const targetArtistId = selectedArtistIdRef.current;
    if (!targetArtistId || !normalizedMessageId) return;
    if (reply?.isUnavailable) {
      toastError('返信元メッセージが見つかりません。');
      return;
    }
    try { await chatTopics.openMessage(normalizedMessageId); }
    catch (error) { toastError(error.message || '返信元メッセージを開けませんでした。'); }
  }, [chatTopics, toastError]);

  const hasInlineReminderSchedule = (message) => {
    if (!message) return false;
    const fields = [
      message.reminder_event_uuid,
      message.reminder_event_name,
      message.reminder_event_performance_type,
      message.reminder_schedule_date,
      message.reminder_start_time,
      message.reminder_end_time,
      message.reminder_note,
      message.reminder_place_name,
      message.reminder_place_address,
    ];
    return fields.some((v) => v != null && v !== '');
  };

  const ensureReminderSchedule = useCallback(
    async (scheduleId) => {
      if (!scheduleId) return null;
      if (reminderCacheRef.current.has(scheduleId)) {
        return reminderCacheRef.current.get(scheduleId);
      }
      try {
        const sched = await getReminderSchedule(scheduleId);
        reminderCacheRef.current.set(scheduleId, sched);
        setReminderCacheVersion((v) => v + 1);
        return sched;
      } catch (_) {
        reminderCacheRef.current.set(scheduleId, null);
        setReminderCacheVersion((v) => v + 1);
        return null;
      }
    },
    [],
  );

  const parseReminderContent = (message) => {
    const cleaned = sanitizeReminderContent(message?.content || '');
    const { scheduleId: parsedId, kind: parsedKind } = parseReminderTags(message?.content || '');
    const scheduleId = message?.reminder_schedule_id || parsedId;
    const kind = message?.reminder_kind || parsedKind;
    const schedFromMsg = {
      event_uuid: message?.reminder_event_uuid || null,
      event_name: message?.reminder_event_name || null,
      event_performance_type: message?.reminder_event_performance_type || null,
      schedule_date: message?.reminder_schedule_date || null,
      start_time: message?.reminder_start_time || null,
      end_time: message?.reminder_end_time || null,
      note: message?.reminder_note || null,
      place_name: message?.reminder_place_name || null,
      place_address: message?.reminder_place_address || null,
    };
    const hasSchedFromMsg = Object.values(schedFromMsg).some((v) => v != null);
    const sched = hasSchedFromMsg
      ? schedFromMsg
      : scheduleId
        ? reminderCacheRef.current.get(scheduleId)
        : null;

    const lines = cleaned
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);

    const eventTitle = sched?.event_name || lines[0] || '';
    const eventPerf   = sched?.event_performance_type || '';
    let scheduleTitle = sched?.note || '';
    let dateTime = '';
    let place = '';

    const isDateLine = (line) => /^\d{4}-\d{2}-\d{2}/.test(line);

    for (const line of lines.slice(1)) {
      if (!dateTime && isDateLine(line)) {
        dateTime = line;
        continue;
      }
      if (!place && line.includes('/')) {
        place = line;
        continue;
      }
      if (!scheduleTitle) {
        scheduleTitle = line;
      }
    }

    if (!scheduleTitle) scheduleTitle = 'スケジュール';

    if (sched) {
      const date = sched.schedule_date ? toYmdSlash(sched.schedule_date) : '';
      const start = sched.start_time ? String(sched.start_time).slice(0, 5) : '';
      const end = sched.end_time ? String(sched.end_time).slice(0, 5) : '';
      dateTime = [date, [start, end].filter(Boolean).join('〜')].filter(Boolean).join(' ');
      const placeBits = [sched.place_name, sched.place_address].filter(Boolean);
      place = placeBits.join(' / ');
    }

    return { eventTitle, eventPerf, scheduleTitle, dateTime, place, scheduleId, kind, schedule: sched };
  };

  const pickFlyerFilename = (message) => {
    if (!message) return '';
    const fmt = message.message_format;
    const isHold = isHoldAnnouncementFormat(fmt);
    const candidates = [
      isHold && message.is_multiple_events ? message.base_event_flyer_front_url : null,
      !isHold && message.contract_is_multi ? message.contract_base_event_flyer_front_url : null,
      isHold ? message.event_flyer_front_url : message.contract_event_flyer_front_url,
      isHold ? message.base_event_flyer_front_url : message.contract_base_event_flyer_front_url,
      message.flyer_front_url,
    ];
    return candidates.find((v) => !!v) || '';
  };

  const resolveFlyerEventUuid = (message) => {
    if (!message) return '';
    const fmt = message.message_format;
    const isHold = isHoldAnnouncementFormat(fmt);
    const candidates = [
      isHold ? message.base_event_uuid : null,
      !isHold ? message.contract_base_event_uuid : null,
      isHold ? message.event_uuid : null,
      !isHold ? message.contract_event_uuid : null,
      message.base_event_uuid,
      message.event_uuid,
      message.contract_event_uuid,
    ];
    return candidates.find((v) => !!v) || '';
  };

  const ensureFlyerUrl = useCallback(
    async (message) => {
      if (
        !message ||
        ![
          ADMIN_MESSAGE_FORMATS.ANNOUNCE_HOLD,
          ADMIN_MESSAGE_FORMATS.ANNOUNCE_HOLD_CANCELLED,
          ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT,
          ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT_CANCELLED,
          ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT_UPDATED,
        ].includes(message.message_format)
      ) {
        return null;
      }

      const cacheKey = String(message.id || `${message.message_format}-${message.created_at}`);
      if (flyerCacheRef.current.has(cacheKey)) {
        return flyerCacheRef.current.get(cacheKey);
      }

      const direct = pickFlyerFilename(message);
      if (direct) {
        const url = buildFlyerUrl(direct);
        flyerCacheRef.current.set(cacheKey, url);
        setFlyerCacheVersion((v) => v + 1);
        return url;
      }

      const targetUuid = resolveFlyerEventUuid(message);
      if (!targetUuid) {
        flyerCacheRef.current.set(cacheKey, null);
        return null;
      }

      try {
        // 1st: baseイベント
        const event = await getEventDetail(targetUuid);
        const data = event ? { event } : {};
        if (event) {
          const remoteFlyer =
            data?.event?.flyer_front_url || data?.event?.flyer_front || data?.flyer_front_url;
          if (remoteFlyer) {
            const url = buildFlyerUrl(remoteFlyer);
            flyerCacheRef.current.set(cacheKey, url);
            setFlyerCacheVersion((v) => v + 1);
            return url;
          }
          // 2nd: 親イベントを参照する場合
          const wantsParent =
            (isHoldAnnouncementFormat(message.message_format)
              ? message.event_use_existing_flyers
              : message.contract_event_use_existing_flyers) &&
            (message.event_original_event_uuid || message.contract_event_original_event_uuid || data?.event?.original_event_uuid);
          const parentUuid =
            message.event_original_event_uuid ||
            message.contract_event_original_event_uuid ||
            data?.event?.original_event_uuid ||
            null;
          if (wantsParent && parentUuid) {
            const parentEvent = await getEventDetail(parentUuid);
            const pdata = parentEvent ? { event: parentEvent } : {};
            if (parentEvent) {
              const parentFlyer =
                pdata?.event?.flyer_front_url || pdata?.event?.flyer_front || pdata?.flyer_front_url;
              if (parentFlyer) {
                const url = buildFlyerUrl(parentFlyer);
                flyerCacheRef.current.set(cacheKey, url);
                setFlyerCacheVersion((v) => v + 1);
                return url;
              }
            }
          }
        }
      } catch {
        /* noop */
      }

      flyerCacheRef.current.set(cacheKey, null);
      setFlyerCacheVersion((v) => v + 1);
      return null;
    },
    [],
  );

  const getCachedFlyerUrl = useCallback(
    (cacheKey) => flyerCacheRef.current.get(cacheKey),
    [],
  );

  const formatMessageContent = (content) => {
    if (!content) return '';
    const urlPattern = /(https?:\/\/[^\s]+)/g;
    return content.split('\n').map((line, index) => (
      <React.Fragment key={index}>
        {line.split(urlPattern).map((part, index2) => {
          if (!/^https?:\/\//.test(part)) return part;
          const displayUrl = normalizeStageSeatingShareUrlForAdmin(part);
          return (
            <a key={index2} href={displayUrl} target="_blank" rel="noopener noreferrer">
              {displayUrl}
            </a>
          );
        })}
        <br />
      </React.Fragment>
    ));
  };
  const extractFirstUrl = (content) => {
    if (!content) return '';
    const match = replaceStageSeatingShareUrlsForAdmin(content).match(/https?:\/\/[^\s]+/);
    return match ? match[0] : '';
  };
  const getHoldDeclineLink = (message) => {
    const directId = message?.hold_casting_artist_id;
    if (directId) return `/admin/workspaces/casting/offers/${directId}`;
    const holdCastingId = message?.hold_casting_id;
    const artistId = message?.artist_id || selectedArtist?.artist_id;
    if (holdCastingId && artistId) {
      return `/admin/workspaces/casting/offers/by-hold/${holdCastingId}?artist_id=${artistId}`;
    }
    return '';
  };

  const getMessageClass = useCallback((message) => {
    const classes = [];
    const system = isSystemFormat(message.message_format);
    if (system) {
      classes.push('system-message');
    } else {
      classes.push(message.message_type === 'admin_message' ? 'admin-message' : 'artist-message');
    }
    if (isHoldAnnouncementFormat(message.message_format)) classes.push('announce-hold-message');
    if (message.message_format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT) classes.push('announce-contract-message');
    if (message.message_format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT_CANCELLED)
      classes.push('announce-contract-cancelled-message');
    if (message.message_format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONTRACT_UPDATED)
      classes.push('announce-contract-updated-message');
    if (message.message_format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_HOLD_DECLINE)
      classes.push('announce-hold-decline-message');
    if (message.message_format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_CONCIERGE_RESULT)
      classes.push('announce-concierge-result-message');
    if (message.message_format === ADMIN_MESSAGE_FORMATS.ANNOUNCE_CANCEL_COMP_DECIDED)
      classes.push('announce-cancel-comp-decided-message');
    if (isInvoiceResubmissionRequestedFormat(message.message_format))
      classes.push('announce-cancel-comp-decided-message');
    if (isPartAnnouncementFormat(message.message_format))
      classes.push('announce-part-assigned-message');
    if (isStageSeatingShareAnnouncementFormat(message.message_format))
      classes.push('announce-stage-seating-shared-message');
    if (message.message_format === ADMIN_MESSAGE_FORMATS.REMINDER_NOTICE) classes.push('reminder-notice-message');
    return appendBubbleFormatClassName(classes.join(' '), message.message_format);
  }, []);

  // ====== ジャンル関連（ID対応） ======
  const parsePgIntArray = useCallback((s) => {
    const t = String(s || '').trim();
    if (t.startsWith('{') && t.endsWith('}')) {
      return t
        .slice(1, -1)
        .split(',')
        .map((x) => Number(x))
        .filter(Number.isInteger);
    }
    return null;
  }, []);
  const normalizeGenreIds = useCallback((maybeGenre) => {
    if (Array.isArray(maybeGenre)) {
      const arr = maybeGenre.map((v) => Number(v)).filter((n) => Number.isInteger(n));
      return arr.length ? arr : [];
    }
    if (typeof maybeGenre === 'string' && maybeGenre.trim().startsWith('{')) {
      return parsePgIntArray(maybeGenre) || [];
    }
    if (typeof maybeGenre === 'string' && maybeGenre.trim().startsWith('[')) {
      try {
        const arr = JSON.parse(maybeGenre);
        if (Array.isArray(arr)) return arr.map((v) => Number(v)).filter((n) => Number.isInteger(n));
      } catch {}
    }
    if (typeof maybeGenre === 'number') return [Number(maybeGenre)];
    if (typeof maybeGenre === 'string' && maybeGenre) return [maybeGenre];
    return [];
  }, [parsePgIntArray]);
  const genresById = useMemo(() => {
    const m = new Map();
    for (const g of genres) m.set(Number(g.id), g);
    return m;
  }, [genres]);
  const genresByValueLower = useMemo(() => {
    const m = new Map();
    for (const g of genres) if (g?.value) m.set(String(g.value).toLowerCase(), g);
    return m;
  }, [genres]);
  const resolvePrimaryGenreObj = useCallback((maybeGenre) => {
    const ids = normalizeGenreIds(maybeGenre);
    if (ids.length > 0 && Number.isInteger(ids[0])) {
      return genresById.get(Number(ids[0])) || null;
    }
    if (
      typeof maybeGenre === 'string' &&
      maybeGenre &&
      !maybeGenre.trim().startsWith('{') &&
      !maybeGenre.trim().startsWith('[')
    ) {
      return genresByValueLower.get(maybeGenre.toLowerCase()) || null;
    }
    return null;
  }, [genresById, genresByValueLower, normalizeGenreIds]);
  const getGenreLabel = useCallback((maybeGenre) => (resolvePrimaryGenreObj(maybeGenre)?.label ?? ''), [resolvePrimaryGenreObj]);
  const getGenreColor = useCallback((maybeGenre) => (resolvePrimaryGenreObj(maybeGenre)?.color ?? 'var(--event-chat-action-blue)'), [resolvePrimaryGenreObj]);
  const getGenreTextColor = useCallback((maybeGenre) => (resolvePrimaryGenreObj(maybeGenre)?.text_color ?? 'var(--color-inverse)'), [resolvePrimaryGenreObj]);

  useEffect(() => {
    genresByIdRef.current = genresById;
    genresByValueLowerRef.current = genresByValueLower;
  }, [genresById, genresByValueLower]);

  const getGenreLabelFromCache = useCallback((maybeGenre) => {
    const ids = normalizeGenreIds(maybeGenre);
    if (ids.length > 0 && Number.isInteger(ids[0])) {
      return genresByIdRef.current.get(Number(ids[0]))?.label ?? '';
    }
    if (
      typeof maybeGenre === 'string' &&
      maybeGenre &&
      !maybeGenre.trim().startsWith('{') &&
      !maybeGenre.trim().startsWith('[')
    ) {
      return genresByValueLowerRef.current.get(maybeGenre.toLowerCase())?.label ?? '';
    }
    return '';
  }, [normalizeGenreIds]);

  const getProfileImageStyle = (artist) => {
    const profilePicture = normalizeAvatarImageValue(artist?.profile_picture);
    if (profilePicture) {
      return {
        backgroundImage: `url(${appendVariant(buildAssetUrl(ARTIST_PROFILE_IMAGE_PATH, profilePicture), 'proxy')})`,
        backgroundSize: 'cover',
      };
    } else {
      let backgroundColor = 'var(--person-avatar-artist-unknown-bg)';
      if (artist.gender === 'male') backgroundColor = 'var(--person-avatar-artist-male-bg)';
      else if (artist.gender === 'female') backgroundColor = 'var(--person-avatar-artist-female-bg)';
      return {
        backgroundColor,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        color: '#fff',
      };
    }
  };

  const buildArtistProfilePayload = useCallback(
    (artist) => {
      if (!artist) return null;
      const detail = artistDetail || {};
      const name = firstNonEmpty(artist?.name, detail?.artist_name);
      if (!name) return null;
      const partLabel = firstNonEmpty(
        artist?.part_label,
        detail?.main_part_label,
        detail?.part_label,
        detail?.part_name
      );
      return buildArtistProfileForModal({
        artist: {
          ...detail,
          ...artist,
          id: artist?.artist_id || detail?.artist_id || null,
          artist_id: artist?.artist_id || detail?.artist_id || null,
          name,
          artist_name: name,
          name_en: firstNonEmpty(detail?.artist_name_en, artist?.name_en),
          artist_name_en: firstNonEmpty(detail?.artist_name_en, artist?.name_en),
          pic: firstNonEmpty(
            artist?.profile_picture,
            detail?.profile_picture,
            detail?.artist_profile_picture
          ),
          artist_profile_picture: firstNonEmpty(
            detail?.artist_profile_picture,
            detail?.profile_picture,
            artist?.profile_picture
          ),
          partLabel,
          part: partLabel,
          instagram_url: firstNonEmpty(detail?.instagram_url, artist?.instagram_url),
          twitter_url: firstNonEmpty(detail?.twitter_url, artist?.twitter_url),
          facebook_url: firstNonEmpty(detail?.facebook_url, artist?.facebook_url),
          hp_url: firstNonEmpty(detail?.hp_url, detail?.homepage_url, artist?.hp_url),
          career_timeline: Array.isArray(detail?.career_timeline)
            ? detail.career_timeline
            : (Array.isArray(artist?.career_timeline) ? artist.career_timeline : []),
          profile_one_liner: firstNonEmpty(
            detail?.profile_one_liner,
            detail?.one_liner,
            detail?.comment,
            detail?.notes
          ),
        },
      });
    },
    [artistDetail]
  );

  const openSelectedArtistProfile = useCallback(() => {
    const payload = buildArtistProfilePayload(selectedArtist);
    if (!payload) return;
    openArtistProfile(payload);
  }, [buildArtistProfilePayload, openArtistProfile, selectedArtist]);

  useEffect(() => {
    closeProfileModal();
  }, [closeProfileModal, selectedArtistId]);

  const isViewingLatest = useCallback(() => {
    const c = chatContainerRef.current;
    if (!c) return true;
    const dir = (getComputedStyle(c).flexDirection || '').toLowerCase();
    if (dir.includes('column-reverse')) {
      return Math.abs(c.scrollTop) <= 60;
    }
    return c.scrollHeight - c.clientHeight - c.scrollTop <= 60;
  }, []);

  const scrollToLatest = useCallback(() => {
    const c = chatContainerRef.current;
    if (!c) return;
    const dir = (getComputedStyle(c).flexDirection || '').toLowerCase();
    c.scrollTop = dir.includes('column-reverse') ? 0 : c.scrollHeight;
  }, []);

  const scrollToTop = useCallback(() => {
    setTimeout(() => {
      scrollToLatest();
    }, 0);
  }, [scrollToLatest]);
  const scrollSidebarToTop = useCallback(() => {
    const el = sidebarRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTop = 0;
    });
  }, []);

  // ニックネーム許可状態の取得
  const nicknamePermissionCacheRef = useRef(new Map());
  const readSettingsInflightRef = useRef(new Map());

  const fetchNicknamePermissionStatus = useCallback(
    async (aId) => {
      if (!aId) {
        setIsNicknameAllowed(false);
        return;
      }
      const cacheKey = String(aId);
      if (nicknamePermissionCacheRef.current.has(cacheKey)) {
        const allowed = nicknamePermissionCacheRef.current.get(cacheKey);
        setIsNicknameAllowed(Boolean(allowed));
        return;
      }
      try {
        const data = await listAdminNicknamePermissions(String(aId));
        const list = Array.isArray(data.permissions) ? data.permissions : [];
        const allowed = list.includes(user?.userId || -1);
        nicknamePermissionCacheRef.current.set(cacheKey, allowed);
        setIsNicknameAllowed(allowed);
      } catch {
        nicknamePermissionCacheRef.current.set(cacheKey, false);
        setIsNicknameAllowed(false);
      }
    },
    [user?.userId],
  );

  const fetchReadReceiptSettings = useCallback(
    async (aId, { withIndicator = false } = {}) => {
      if (!aId) {
        setIsReadNotifyEnabled(false);
        setReadEnabledAdmins([]);
        return false;
      }
      const cacheKey = String(aId);
      if (readSettingsInflightRef.current.has(cacheKey)) {
        return readSettingsInflightRef.current.get(cacheKey);
      }
      if (withIndicator) setReadNotifyFetching(true);
      const requestPromise = (async () => {
        try {
          const data = await getAdminReadReceiptSetting(String(aId));
          const enabled = Boolean(data.enabled);
          const admins = Array.isArray(data.enabledAdmins) ? data.enabledAdmins : [];
          const activeSettingsId = settingsArtistIdRef.current || selectedArtistIdRef.current;
          readOptInCacheRef.current.set(String(aId), enabled);
          bumpReadOptInVersion();
          if (String(activeSettingsId) === String(aId)) {
            setIsReadNotifyEnabled(enabled);
            setReadEnabledAdmins(admins);
          }
          return enabled;
        } catch (err) {
          console.error('[readReceipt] fetch error:', err);
          const activeSettingsId = settingsArtistIdRef.current || selectedArtistIdRef.current;
          if (String(activeSettingsId) === String(aId)) {
            setIsReadNotifyEnabled(false);
            setReadEnabledAdmins([]);
          }
          return false;
        } finally {
          if (withIndicator) setReadNotifyFetching(false);
        }
      })();
      readSettingsInflightRef.current.set(cacheKey, requestPromise);
      const result = await requestPromise;
      readSettingsInflightRef.current.delete(cacheKey);
      return result;
    },
    [bumpReadOptInVersion],
  );

  const fetchArtistMuteStatus = useCallback(
    async (aId, { withIndicator = false } = {}) => {
      if (!aId) {
        setArtistChatMuted(false);
        return false;
      }
      const cacheKey = String(aId);
      if (artistMuteCacheRef.current.has(cacheKey)) {
        const cached = artistMuteCacheRef.current.get(cacheKey);
        setArtistChatMuted(Boolean(cached));
        return cached;
      }
      if (withIndicator) setArtistChatMuteLoading(true);
      try {
        const data = await getAdminArtistChatNotifyMute(String(aId));
        const muted = !!data.muted;
        const activeSettingsId = settingsArtistIdRef.current || selectedArtistIdRef.current;
        artistMuteCacheRef.current.set(cacheKey, muted);
        if (String(activeSettingsId) === cacheKey) {
          setArtistChatMuted(muted);
        }
        return muted;
      } catch (err) {
        console.error('[artist-chat mute] fetch error:', err);
        artistMuteCacheRef.current.set(cacheKey, false);
        const activeSettingsId = settingsArtistIdRef.current || selectedArtistIdRef.current;
        if (String(activeSettingsId) === cacheKey) {
          setArtistChatMuted(false);
        }
        return false;
      } finally {
        if (withIndicator) setArtistChatMuteLoading(false);
      }
    },
    [],
  );

  const markMessagesAsRead = useCallback(async (aId) => {
    if (String(aId || '') !== chatTopics.conversationId) return false;
    try { return await chatTopics.markRead(); } catch { return false; }
  }, [chatTopics]);

  // ★★★ 選択アーティストの保存・復元ユーティリティ（ユーザー別＋共通キー）
  const BASE_LAST_KEY = 'admin-artistchat:last';
  const userLastKey = useMemo(() => (user?.userId ? `${BASE_LAST_KEY}:${user.userId}` : null), [user?.userId]);
  const queryArtistId = useMemo(() => {
    try {
      const params = new URLSearchParams(location.search || '');
      return normalizeArtistId(
        params.get('artistId') ||
          params.get('artist') ||
          params.get('artist_id') ||
          ''
      );
    } catch {
      return '';
    }
  }, [location.search]);
  const queryAnchorMessageId = useMemo(() => {
    try {
      const params = new URLSearchParams(location.search || '');
      return normalizeMessageId(
        params.get('messageId') ||
          params.get('message') ||
          params.get('message_id') ||
          ''
      );
    } catch {
      return '';
    }
  }, [location.search]);

  const saveLast = useCallback(
    (artist) => {
      if (!artist) return;
      const obj = { artistId: String(artist.artist_id), name: artist.name || '', ts: Date.now() };
      try {
        if (userLastKey) sessionStorage.setItem(userLastKey, JSON.stringify(obj));
        sessionStorage.setItem(BASE_LAST_KEY, JSON.stringify(obj));
      } catch {}
    },
    [userLastKey],
  );

  const clearLast = useCallback(() => {
    try {
      if (userLastKey) sessionStorage.removeItem(userLastKey);
      sessionStorage.removeItem(BASE_LAST_KEY);
    } catch {}
  }, [userLastKey]);

  useEffect(() => {
    if (!isMobile || mobileView !== 'list') return;
    if (!mobileListPrefRef.current) return;

    pendingRestoreRef.current = false;
    clearLast();
    try {
      sessionStorage.removeItem('selectedArtistId');
      sessionStorage.removeItem('selectedArtistName');
    } catch {}

    selectedArtistIdRef.current = null;
    selectedArtistRef.current = null;
    prevSelectedArtistIdRef.current = null;
    setSelectedArtist(null);
    setMessages([]);
    setOldestCursor(null);
    setHasMore(true);
    setHasNewerMessages(false);
    setMessageWindowOffset(0);
    setActiveAnchorMessageId('');
  }, [isMobile, mobileView, clearLast]);

  useEffect(
    () => () => {
      if (anchorHighlightTimerRef.current) {
        clearTimeout(anchorHighlightTimerRef.current);
        anchorHighlightTimerRef.current = null;
      }
    },
    [],
  );

  // サイドバーからの“未選択で開く”リセット要求の抑止フラグ
  const suppressRestoreRef = useRef(false);

  const mapMessagesForDisplay = useCallback(
    async (rawMessages = []) =>
      Promise.all(
        (Array.isArray(rawMessages) ? rawMessages : []).map(async (message) => {
          let admin_name = '';
          if (
            message.message_type === 'admin_message' &&
            message.admin_user_id &&
            !shouldExcludeAdminSenderForMessage(message)
          ) {
            try {
              const adminData = await getAdminUser(Number(message.admin_user_id));
              admin_name = adminData
                ? message.is_nickname_allowed
                  ? adminData.nickname
                  : adminData.full_name || adminData.username
                : '';
            } catch {
              admin_name = '';
            }
          }
          return {
            ...message,
            admin_name,
            isSystem: isSystemFormat(message.message_format),
            className: getMessageClass(message),
            genreLabel:
              isHoldAnnouncementFormat(message.message_format)
                ? getGenreLabel(message.event_genre, genres)
                : undefined,
          };
        }),
      ),
    [genres, getGenreLabel, getMessageClass],
  );

  // 初回/再取得
  const fetchInitialMessages = useCallback(async (aId, { force = false, anchorMessageId = '' } = {}) => {
    const targetArtistId = normalizeArtistId(aId);
    setLoadedMessagesArtistId('');
    setIsInitialLoading(true);
    setIsFetching(true);
    const normalizedAnchorMessageId = String(anchorMessageId || '').trim();
    try {
      const data = normalizedAnchorMessageId
        ? await listAdminMessagesAround(String(aId), normalizedAnchorMessageId, 25, 25)
        : await listAdminMessages(String(aId), 0, limit);

      const messagesWithClassNames = dedupeMessages(await mapMessagesForDisplay(data.messages || []));

      if (selectedArtistIdRef.current !== aId) {
        setIsFetching(false);
        setIsInitialLoading(false);
        return messagesWithClassNames;
      }

      setMessages(messagesWithClassNames);
      const cursor =
        messagesWithClassNames.length > 0
          ? messagesWithClassNames[messagesWithClassNames.length - 1].created_at
          : null;
      setOldestCursor(cursor);
      if (normalizedAnchorMessageId) {
        const startOffset = Number(data?.offset || 0);
        setHasMore(!!data?.hasOlder);
        setHasNewerMessages(!!data?.hasNewer);
        setMessageWindowOffset(startOffset);
        focusChatAnchorMessage({
          messageId: normalizedAnchorMessageId,
          containerRef: chatContainerRef,
          setActiveAnchorMessageId,
          timerRef: anchorHighlightTimerRef,
          behavior: 'auto',
        });
      } else {
        setHasMore(messagesWithClassNames.length === limit);
        setHasNewerMessages(false);
        setMessageWindowOffset(0);
        setActiveAnchorMessageId('');
      }
      return messagesWithClassNames;
    } catch (error) {
      if (isTopicRequestSuperseded(error)) return;
      if (normalizedAnchorMessageId && Number(error?.status) === 404) {
        try {
          const fallbackData = await listAdminMessages(String(aId), 0, limit);
          const fallbackMessages = dedupeMessages(await mapMessagesForDisplay(fallbackData.messages || []));
          if (selectedArtistIdRef.current !== aId) return fallbackMessages;
          setMessages(fallbackMessages);
          const cursor =
            fallbackMessages.length > 0
              ? fallbackMessages[fallbackMessages.length - 1].created_at
              : null;
          setOldestCursor(cursor);
          setHasMore(fallbackMessages.length === limit);
          setHasNewerMessages(false);
          setMessageWindowOffset(0);
          setActiveAnchorMessageId('');
          toastError('対象メッセージが見つからないため、最新メッセージを表示しました。');
          return fallbackMessages;
        } catch {}
      }
      toastError('メッセージの取得に失敗しました。');
    } finally {
      if (isSameArtistId(selectedArtistIdRef.current, targetArtistId)) {
        setLoadedMessagesArtistId(targetArtistId);
      }
      setIsFetching(false);
      setIsInitialLoading(false);
    }
  }, [limit, mapMessagesForDisplay, toastError]);
  fetchInitialMessagesRef.current = fetchInitialMessages;

  // NOTE(Anchored Timeline v1 §4.1.2): 旧 scheduleRealtimeMessageReconcile(700ms 後に
  // 最新ページを再取得して merge する第4経路)は廃止。socket 受信は
  // 「enriched payload の直接 merge」+「スケルトン時のみ new-messages 差分フェッチ」の
  // 2経路に集約した(handleNewMessage 参照)。read receipt の scheduleReconcile とは別物。

  const fetchOlderMessages = useCallback(
    async () => {
      if (!selectedArtist || !hasMore || isFetching) return;

      const c = chatContainerRef.current;
      let anchorId = null;
      let anchorTop = 0;
      if (c) {
        const cRect = c.getBoundingClientRect();
        const rows = Array.from(c.querySelectorAll('[data-mid]'));
        const firstVisible = rows.find((el) => el.getBoundingClientRect().top >= cRect.top - 1);
        if (firstVisible) {
          anchorId = firstVisible.getAttribute('data-mid');
          anchorTop = firstVisible.getBoundingClientRect().top;
        }
      }

      setIsFetching(true);
      setLoadingOlderMessages(true);
      try {
        const isAnchorMode = !!activeAnchorMessageId;
        const data = isAnchorMode
          ? await listAdminMessages(
              String(selectedArtist.artist_id),
              messageWindowOffset + messages.length,
              limit,
            )
          : oldestCursor
            ? await listAdminMessagesBefore(String(selectedArtist.artist_id), oldestCursor, limit)
            : null;
        if (!data) return;

        const older = await mapMessagesForDisplay(data.messages || []);

        setMessages((prev) => mergeMessages(prev, older, { getKey: getMessageDedupKey }));
        if (older.length) setOldestCursor(older[older.length - 1].created_at);
        setHasMore(older.length === limit);

        requestAnimationFrame(() => {
          if (!c || !anchorId) return;
          const sameRow = c.querySelector(`[data-mid="${anchorId}"]`);
          if (!sameRow) return;
          const newTop = sameRow.getBoundingClientRect().top;
          const dy = newTop - anchorTop;
          // §4.1.3: column-reverse のブラウザ標準アンカリング(bottom基準)により
          // older を prepend しても可視位置は本来動かない = dy≈0。よって通常は no-op。
          // Safari 等でごく稀に残差がある場合のみ補正するガードに縮退(実測 dy が 1px 超のときだけ適用)。
          if (Math.abs(dy) > 1) c.scrollTop += dy;
        });
      } catch (error) {
        if (isTopicRequestSuperseded(error)) return;
        toastError('メッセージの取得に失敗しました。');
      } finally {
        setLoadingOlderMessages(false);
        setIsFetching(false);
        setIsInitialLoading(false);
      }
    },
    [
      activeAnchorMessageId,
      hasMore,
      isFetching,
      limit,
      mapMessagesForDisplay,
      messageWindowOffset,
      messages.length,
      oldestCursor,
      selectedArtist,
      toastError,
    ],
  );

  const loadNewerMessages = useCallback(async () => {
    if (
      !selectedArtist ||
      !hasNewerMessages ||
      loadingNewerMessages ||
      isFetching
    ) {
      return;
    }

    const startOffset = Math.max(0, messageWindowOffset - limit);
    const fetchLimit = Math.max(1, messageWindowOffset - startOffset);
    const c = chatContainerRef.current;
    let anchorId = null;
    let anchorTop = 0;
    if (c) {
      const cRect = c.getBoundingClientRect();
      const rows = Array.from(c.querySelectorAll('[data-mid]'));
      const firstVisible = rows.find((el) => el.getBoundingClientRect().top >= cRect.top - 1);
      if (firstVisible) {
        anchorId = firstVisible.getAttribute('data-mid');
        anchorTop = firstVisible.getBoundingClientRect().top;
      }
    }

    setLoadingNewerMessages(true);
    try {
      const data = await listAdminMessages(String(selectedArtist.artist_id), startOffset, fetchLimit);
      const newerRows = await mapMessagesForDisplay(data.messages || []);
      setMessages((prev) => mergeMessages(prev, newerRows, { getKey: getMessageDedupKey }));
      setMessageWindowOffset(startOffset);
      setHasNewerMessages(startOffset > 0);

      requestAnimationFrame(() => {
        if (!c || !anchorId) return;
        const sameRow = c.querySelector(`[data-mid="${anchorId}"]`);
        if (!sameRow) return;
        const newTop = sameRow.getBoundingClientRect().top;
        const dy = newTop - anchorTop;
        // §4.1.3: ANCHOR モードで newer を prepend する経路。column-reverse の
        // bottom アンカリングで可視位置は通常維持される(dy≈0)。実測残差が 1px 超の
        // ときのみ補正する縮退ガード(Safari 等のフォールバック)。
        if (Math.abs(dy) > 1) c.scrollTop += dy;
      });
    } catch (error) {
      if (isTopicRequestSuperseded(error)) return;
      toastError('メッセージの取得に失敗しました。');
    } finally {
      setLoadingNewerMessages(false);
    }
  }, [
      hasNewerMessages,
    isFetching,
    limit,
    loadingNewerMessages,
    mapMessagesForDisplay,
    messageWindowOffset,
    selectedArtist,
    toastError,
  ]);

  const buildArtistsQuery = ({ offset = 0 } = {}) => {
    const query = { limit: ARTISTS_PAGE_SIZE, offset };
    if (nameFilter.trim()) query.name = nameFilter.trim();
    if (partFilter) query.partLabel = partFilter;
    if (groupFilter) query.groupId = Number(groupFilter);
    if (messageFilter.trim()) query.message = messageFilter.trim();
    if (castingFilter.eventId) {
      query.castingEventId = castingFilter.eventId;
      query.castingKind = castingFilter.kind;
      if (castingFilter.status) query.castingStatus = castingFilter.status;
    }
    return query;
  };

  const prefetchReadReceiptSettings = useCallback(
    (rows) => {
      if (!Array.isArray(rows) || rows.length === 0) return;
      let seededFromRows = false;
      rows.forEach((artist) => {
        const key = normalizeArtistId(artist?.artist_id);
        if (!key) return;
        const rowEnabled = artist?.read_notify_enabled ?? artist?.readNotifyEnabled;
        if (rowEnabled === true || rowEnabled === false) {
          readOptInCacheRef.current.set(key, Boolean(rowEnabled));
          seededFromRows = true;
          return;
        }
        if (readOptInCacheRef.current.has(key)) return;
        fetchReadReceiptSettings(key, { withIndicator: false });
      });
      if (seededFromRows) {
        bumpReadOptInVersion();
      }
    },
    [bumpReadOptInVersion, fetchReadReceiptSettings],
  );

  const fetchArtistsPage = async ({ reset = false, retried = false } = {}) => {
    if (checking) return;
    if (!canViewArtistTargets) {
      artistTargetAccessDeniedRef.current = false;
      artistsLoadingRef.current = false;
      setArtists([]);
      setArtistOffset(0);
      setArtistsHasMore(false);
      setArtistsLoading(false);
      return;
    }
    if (artistTargetAccessDeniedRef.current) return;
    if (artistsLoadingRef.current) return;
    artistsLoadingRef.current = true;
    setArtistsLoading(true);

    const seq = ++artistsFetchSeqRef.current;
    const offset = reset ? 0 : artistOffset;
    const isMessageSearchMode = !!String(messageFilter || '').trim();
    const query = buildArtistsQuery({ offset });

    try {
      const data = await searchAdminChatArtistsWithPartsPaged(query);
      if (seq !== artistsFetchSeqRef.current) return;

      const rows = (Array.isArray(data.artists) ? data.artists : []).map(normalizeArtistSidebarRow);
      if (reset) {
        if (isMessageSearchMode) {
          loadedArtistIdsRef.current = new Set();
        } else {
          loadedArtistIdsRef.current = new Set(
            rows.map((a) => normalizeArtistId(a.artist_id)).filter((id) => id),
          );
        }
        setArtists(() => rows);
        setArtistOffset(data.nextOffset ?? rows.length);
        setArtistsHasMore(!!data.hasMore);
        prefetchReadReceiptSettings(rows);
      } else {
        if (isMessageSearchMode) {
          setArtists((prev) => [...prev, ...rows]);
        } else {
          const incoming = rows.filter((a) => !loadedArtistIdsRef.current.has(normalizeArtistId(a.artist_id)));
          incoming.forEach((a) => {
            const id = normalizeArtistId(a.artist_id);
            if (id) loadedArtistIdsRef.current.add(id);
          });
          setArtists((prev) => [...prev, ...incoming]);
          prefetchReadReceiptSettings(incoming);
        }
        setArtistOffset(data.nextOffset ?? offset + rows.length);
        setArtistsHasMore(!!data.hasMore);
        if (isMessageSearchMode) {
          prefetchReadReceiptSettings(rows);
        }
      }
    } catch (error) {
      if (Number(error?.status) === 403) {
        const refreshed = await fetchSession?.().catch(() => null);
        const stillAllowed = canViewPersonalChatForSession(refreshed);
        if (stillAllowed && !retried) {
          artistTargetAccessDeniedRef.current = false;
          artistsLoadingRef.current = false;
          setArtistsLoading(false);
          await wait(300);
          return fetchArtistsPage({ reset, retried: true });
        }
        if (!stillAllowed) {
          artistTargetAccessDeniedRef.current = true;
          setArtists([]);
          setArtistOffset(0);
          setArtistsHasMore(false);
        } else if (!artistTargetsRetryTimerRef.current) {
          artistTargetsRetryTimerRef.current = window.setTimeout(() => {
            artistTargetsRetryTimerRef.current = null;
            fetchArtistsPage({ reset, retried: false });
          }, 1200);
        }
        return;
      }
      toastError('アーティスト一覧の取得に失敗しました。');
    } finally {
      if (seq === artistsFetchSeqRef.current) {
        artistsLoadingRef.current = false;
        setArtistsLoading(false);
      }
    }
  };

  const fetchOneArtistAndPrepend = useCallback(async (aId, { retried = false } = {}) => {
    if (castingFilter.eventId || !canViewArtistTargets || artistTargetAccessDeniedRef.current) return false;
    try {
      const { artist: artistRaw } = await getAdminChatArtistWithParts(String(aId));
      const artistRow = normalizeArtistSidebarRow(artistRaw);
      if (!artistRow) return false;
      const artistKey = normalizeArtistId(artistRow.artist_id);
      if (!artistKey) return false;
      if (!loadedArtistIdsRef.current.has(artistKey)) {
        loadedArtistIdsRef.current.add(artistKey);
        setArtists((prev) => [artistRow, ...prev]);
      } else {
        setArtists((prev) => {
          const idx = prev.findIndex((a) => isSameArtistId(a.artist_id, artistRow.artist_id));
          if (idx === -1) return prev;
          const updated = prev[idx];
          const rest = prev.slice(0, idx).concat(prev.slice(idx + 1));
          return [updated, ...rest];
        });
      }
      return true;
    } catch (error) {
      if (Number(error?.status) === 403) {
        const refreshed = await fetchSession?.().catch(() => null);
        const stillAllowed = canViewPersonalChatForSession(refreshed);
        if (stillAllowed && !retried) {
          artistTargetAccessDeniedRef.current = false;
          await wait(300);
          return fetchOneArtistAndPrepend(aId, { retried: true });
        }
        if (!stillAllowed) {
          artistTargetAccessDeniedRef.current = true;
        }
        return false;
      }
      return false;
    }
  }, [castingFilter, canViewArtistTargets, canViewPersonalChatForSession, fetchSession, normalizeArtistSidebarRow]);

  const fetchOneArtist = useCallback(async (aId, { retried = false } = {}) => {
    if (!canViewArtistTargets || artistTargetAccessDeniedRef.current) return null;
    try {
      const { artist: artistRaw } = await getAdminChatArtistWithParts(String(aId));
      return normalizeArtistSidebarRow(artistRaw) || null;
    } catch (error) {
      if (Number(error?.status) === 403) {
        const refreshed = await fetchSession?.().catch(() => null);
        const stillAllowed = canViewPersonalChatForSession(refreshed);
        if (stillAllowed && !retried) {
          artistTargetAccessDeniedRef.current = false;
          await wait(300);
          return fetchOneArtist(aId, { retried: true });
        }
        if (!stillAllowed) {
          artistTargetAccessDeniedRef.current = true;
        }
      }
      return null;
    }
  }, [canViewArtistTargets, canViewPersonalChatForSession, fetchSession, normalizeArtistSidebarRow]);

  // ▼ 追加：並び順(最新メッセージ時刻の降順)を維持して1件追加する
  const parseTs = useCallback((s) => {
    if (!s) return 0;
    const x = String(s).trim();
    const iso = x.includes('T') ? x : x.replace(' ', 'T');
    const withSec = /\d{2}:\d{2}:\d{2}$/.test(iso) ? iso : `${iso}:00`;
    const t = Date.parse(withSec);
    return Number.isFinite(t) ? t : 0;
  }, []);

  const insertArtistOrdered = useCallback((row) => {
    if (castingFilter.eventId || !row) return;
    const rowId = normalizeArtistId(row.artist_id);
    if (rowId) loadedArtistIdsRef.current.add(rowId);
    setArtists((prev) => {
      if (prev.some((a) => isSameArtistId(a.artist_id, row.artist_id))) return prev;
      const next = [...prev, row];
      next.sort(
        (a, b) =>
          parseTs(getArtistLatestTimestamp(b)) - parseTs(getArtistLatestTimestamp(a)),
      );
      return next;
    });
  }, [castingFilter, getArtistLatestTimestamp, parseTs]);

  const ensureArtistInList = useCallback(
    async (incomingArtistId) => {
      try {
        const incomingKey = normalizeArtistId(incomingArtistId);
        if (!incomingKey) return { existedBefore: false, existsAfter: false };
        const existedBefore =
          loadedArtistIdsRef.current.has(incomingKey) ||
          artists.some((a) => isSameArtistId(a.artist_id, incomingKey));
        if (existedBefore) return { existedBefore, existsAfter: true };
        const ok = await fetchOneArtistAndPrepend(incomingKey);
        return { existedBefore, existsAfter: ok || loadedArtistIdsRef.current.has(incomingKey) };
      } catch {
        return { existedBefore: false, existsAfter: false };
      }
    },
    [artists, fetchOneArtistAndPrepend],
  );

  useEffect(() => {
    if (!allowAutoAppend) return;
    if (pendingArtistIdsRef.current.size === 0) return;
    (async () => {
      const ids = Array.from(pendingArtistIdsRef.current);
      pendingArtistIdsRef.current.clear();
      for (const id of ids) {
        const artistKey = normalizeArtistId(id);
        if (!artistKey) continue;
        if (loadedArtistIdsRef.current.has(artistKey) || artists.some((a) => isSameArtistId(a.artist_id, artistKey))) {
          continue;
        }
        const row = await fetchOneArtist(artistKey);
        if (!row) continue;
        loadedArtistIdsRef.current.add(artistKey);
        setArtists((prev) => [row, ...prev]);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowAutoAppend]);

  // 初期ロード
  useEffect(() => {
    if (checking) return undefined;
    let cancelled = false;

    loadedArtistIdsRef.current = new Set();
    artistTargetAccessDeniedRef.current = false;
    if (canViewArtistTargets) {
      fetchArtistsPage({ reset: true });
    } else {
      setArtists([]);
      setArtistOffset(0);
      setArtistsHasMore(false);
      setArtistsLoading(false);
    }

    const restoreSavedGenres = () => {
      const savedGenres = localStorage.getItem('genres');
      if (!savedGenres) return;
      try {
        const parsed = JSON.parse(savedGenres);
        const normalized = Array.isArray(parsed)
          ? parsed
          : Array.isArray(parsed?.genres)
          ? parsed.genres
          : null;
        if (normalized) {
          setGenres(normalized);
        } else {
          localStorage.removeItem('genres');
        }
      } catch {
        localStorage.removeItem('genres');
      }
    };

    restoreSavedGenres();

    (async () => {
      try {
        const [partsRes, groupsRes, genresRes] = await Promise.allSettled([
          listParts(),
          listArtistGroups(),
          listGenres(),
        ]);

        if (!cancelled && partsRes.status === 'fulfilled') {
          setParts(partsRes.value);
        }
        if (!cancelled && groupsRes.status === 'fulfilled') {
          setGroups(groupsRes.value);
        }
        if (!cancelled && genresRes.status === 'fulfilled') {
          const normalized = Array.isArray(genresRes.value)
            ? genresRes.value
            : Array.isArray(genresRes.value?.genres)
            ? genresRes.value.genres
            : null;
          if (normalized) {
            setGenres(normalized);
            localStorage.setItem('genres', JSON.stringify(normalized));
          }
        }
      } catch {
        /* noop */
      }
    })();

    return () => {
      cancelled = true;
      if (artistTargetsRetryTimerRef.current) {
        clearTimeout(artistTargetsRetryTimerRef.current);
        artistTargetsRetryTimerRef.current = null;
      }
      if (reconcileTimerRef.current) clearTimeout(reconcileTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewArtistTargets, checking]);

  // 遷移後のフォールバック
  useEffect(() => {
    if (checking) return;
    if (!canViewArtistTargets) return;
    if (!artistsLoadingRef.current && artists.length === 0) {
      fetchArtistsPage({ reset: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key, canViewArtistTargets, checking]);

  // ★★★ 初期選択：サイドバー reset → URL → 旧キー → 最後の選択 → 未選択
  useEffect(() => {
    if (checking) return;
    if (initialSelectionDoneRef.current) return;
    if (!canViewArtistTargets) {
      setIsAiTargetSelected(true);
      setSelectedArtist(null);
      setMessages([]);
      setHasMore(false);
      setHasNewerMessages(false);
      setMessageWindowOffset(0);
      setActiveAnchorMessageId('');
      initialSelectionDoneRef.current = true;
      return;
    }

    (async () => {
      // 1) サイドバーからのリセット要求
      if (location?.state?.reset) {
        suppressRestoreRef.current = true;
        pendingRestoreRef.current = false;
        clearLast();
        try {
          sessionStorage.removeItem('selectedArtistId');
          sessionStorage.removeItem('selectedArtistName');
        } catch {}
        setIsAiTargetSelected(true);
        setSelectedArtist(null);
        setMessages([]);
        setOldestCursor(null);
        setHasMore(true);
        setHasNewerMessages(false);
        setMessageWindowOffset(0);
        setActiveAnchorMessageId('');
        if (isMobile) {
          mobileListPrefRef.current = true;
          mobileChatHistoryRef.current = false;
          setMobileView('list');
        }
        initialSelectionDoneRef.current = true;
        navigate('/admin/workspaces/communications/artist-chat', { replace: true, state: null });
        return;
      }

      // 2) URL パラメータ優先
      const targetArtistIdFromUrl = queryArtistId || normalizeArtistId(artistId);
      if (targetArtistIdFromUrl) {
        let picked = artists.find((a) => isSameArtistId(a.artist_id, targetArtistIdFromUrl));
        if (!picked) {
          const row = await fetchOneArtist(targetArtistIdFromUrl);
          if (row) insertArtistOrdered(row);
          picked =
            artists.find((a) => isSameArtistId(a.artist_id, targetArtistIdFromUrl)) ||
            { artist_id: targetArtistIdFromUrl, name: '' };
        }
        setIsAiTargetSelected(false);
        setSelectedArtist(picked);
        saveLast(picked);
        await fetchInitialMessages(targetArtistIdFromUrl, {
          anchorMessageId: queryAnchorMessageId,
        });
        initialSelectionDoneRef.current = true;
        if (isMobile) {
          mobileListPrefRef.current = false;
          setMobileView('chat');
        }
        if (queryArtistId) {
          navigate('/admin/workspaces/communications/artist-chat', { replace: true });
        }
        return;
      }

      // 3) 旧一時キー（互換）
      const sessionArtistId = sessionStorage.getItem('selectedArtistId');
      const sessionArtistName = sessionStorage.getItem('selectedArtistName');
      if (sessionArtistId) {
        const picked = { artist_id: sessionArtistId, name: sessionArtistName || '' };
        setIsAiTargetSelected(false);
        setSelectedArtist(picked);
        saveLast(picked);
        await fetchInitialMessages(sessionArtistId);
        sessionStorage.removeItem('selectedArtistId');
        sessionStorage.removeItem('selectedArtistName');
        if (isMobile) {
          mobileListPrefRef.current = false;
          setMobileView('chat');
        }
        initialSelectionDoneRef.current = true;
        return;
      }

      // 4) 既定は CasticartAI を開く
      setIsAiTargetSelected(true);
      pendingRestoreRef.current = false;
      initialSelectionDoneRef.current = true;
      if (isMobile) {
        mobileListPrefRef.current = true;
        mobileChatHistoryRef.current = false;
        setMobileView('list');
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artistId, artists.length, location.state, isMobile, canViewArtistTargets, checking]);

  // ★★★ 画面が開いたままサイドバーから再遷移（reset）された場合のホットリセット
  useEffect(() => {
    if (!location?.state?.reset) return;

    suppressRestoreRef.current = true;
    clearLast();

    setIsAiTargetSelected(true);
    setSelectedArtist(null);
    setMessages([]);
    setOldestCursor(null);
    setHasMore(true);
    setHasNewerMessages(false);
    setMessageWindowOffset(0);
    setActiveAnchorMessageId('');
    if (isMobile) {
      mobileListPrefRef.current = true;
      mobileChatHistoryRef.current = false;
      setMobileView('list');
    }

    navigate('/admin/workspaces/communications/artist-chat', { replace: true, state: null });
  }, [location.state, clearLast, navigate, isMobile]);

  useEffect(() => {
    if (checking) return;
    if (!canViewArtistTargets) return;
    artistTargetAccessDeniedRef.current = false;
    artistsFetchSeqRef.current++;
    artistsLoadingRef.current = false;
    setArtists([]);
    loadedArtistIdsRef.current = new Set();
    setArtistOffset(0);
    setArtistsHasMore(true);
    fetchArtistsPage({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canViewArtistTargets, nameFilter, partFilter, groupFilter, messageFilter, castingFilter, checking]);

  useEffect(() => {
    if (checking) return;
    if (!canViewArtistTargets) return;
    if (artistsSentinelInView && artistsHasMore && !artistsLoading) {
      fetchArtistsPage({ reset: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artistsSentinelInView, artistsHasMore, artistsLoading, canViewArtistTargets, checking]);

  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(ADMIN_ARTIST_ASSIGNEE_FILTER_STORAGE_KEY);
      if (saved === 'read-on') setAssigneeFilter('read-on');
    } catch {}
  }, []);

  useEffect(() => {
    try {
      if (assigneeFilter) {
        window.localStorage.setItem(ADMIN_ARTIST_ASSIGNEE_FILTER_STORAGE_KEY, assigneeFilter);
      } else {
        window.localStorage.removeItem(ADMIN_ARTIST_ASSIGNEE_FILTER_STORAGE_KEY);
      }
    } catch {}
  }, [assigneeFilter]);

  useEffect(() => {
    try {
      const normalized = normalizePinnedArtistIds(pinnedArtistIds);
      if (normalized.length) {
        window.localStorage.setItem(ADMIN_ARTIST_PINNED_TARGETS_STORAGE_KEY, JSON.stringify(normalized));
      } else {
        window.localStorage.removeItem(ADMIN_ARTIST_PINNED_TARGETS_STORAGE_KEY);
      }
    } catch {}
  }, [pinnedArtistIds]);

  useEffect(() => {
    prefetchReadReceiptSettings(artists);
  }, [artists, prefetchReadReceiptSettings]);

  return {
    formatSidebarTimestamp, getArtistLatestTimestamp, getSidebarPreviewText, isSystemFormat, canUnsendMessage, canAnnouncementMessage,
    canReactMessage, canReplyMessage, canCopyMessage, canCreateQuickTaskFromMessage, messageMenuRef, targetMenuRef,
    closeMessageMenu, closeTargetMenu, openTargetMenuAt, clearMessageLongPress, handleMessageContextMenu, handleMessageTouchStart,
    handleUnsendMessage, handleToggleAnnouncementMessage, handleMessageMenuReactionSelect, handleCopyMessage, handleOpenInlinePartialCopy, handleOpenQuickTaskFromMessage,
    renderPartialCopyContent, handleSelectReplyMessage, handleSelectAnnouncementMessage, handleJumpToReplyMessage, hasInlineReminderSchedule, ensureReminderSchedule,
    parseReminderContent, pickFlyerFilename, ensureFlyerUrl, getCachedFlyerUrl, formatMessageContent, extractFirstUrl,
    getHoldDeclineLink, getMessageClass, resolvePrimaryGenreObj, getGenreLabel, getGenreColor, getGenreTextColor,
    getGenreLabelFromCache, getProfileImageStyle, openSelectedArtistProfile, isViewingLatest, scrollToLatest, scrollToTop,
    scrollSidebarToTop, fetchNicknamePermissionStatus, fetchReadReceiptSettings, fetchArtistMuteStatus, markMessagesAsRead, saveLast,
    mapMessagesForDisplay, fetchInitialMessages, fetchOlderMessages, loadNewerMessages, parseTs, ensureArtistInList,
  };
}
